import React, { useContext, useEffect, useState } from "react";
import { config } from "../../../constants";
import Waitable from "../../common/waitable/waitable";
import { GameStatus } from "../../../types/game-status";
import { useAppSelector } from "../../../redux/store";
import FormPage from "../../common/formpage/formpage";
import { useFormValidation } from "../../../hooks/useFormValidation";
import { useWaitableForm } from "../../../hooks/useWaitableForm";
import { GameContext } from "../../../contexts";
import {
  selectIsSolo,
  selectGameSettings,
  selectPlayerCurrentStory,
} from "../../../redux/selectors";
import { Maturity } from "../../../types";
import { useTranslation } from "react-i18next";
import { Character } from "../../../types";
import FakeButton from "@/components/fake-button";

const CreateStory = () => {
  const { submit } = useContext(GameContext);

  const playerState = useAppSelector((state) => state.player);
  const currentStory = useAppSelector(selectPlayerCurrentStory);
  const settings = useAppSelector(selectGameSettings);

  const { t } = useTranslation("game");

  let isSolo = useAppSelector(selectIsSolo);

  const numChoices = settings.num_characters_select ?? config.numChoices;

  // const [loading, setLoading] = useState(isLoading)
  const [picks, setPicks] = useState(new Map<number, Character>());
  const [mature, setMature] = useState<boolean | undefined>(undefined);

  const [hasAllAges, setHasAllAges] = useState(false);

  useEffect(() => {
    let temp = false;
    picks.forEach((p) => {
      if (!p.meta.mature) {
        temp = true;
      }
    });
    if (temp) {
      setMature(false);
      setHasAllAges(true);
    } else {
      setHasAllAges(false);
    }
  }, [picks]);

  function gameSubmit() {
    let chars = Array.from(picks.keys()).map((value) => {
      return {
        id: value,
      };
    });

    return {
      body: {
        story: {
          id: currentStory?.id,
          characters: chars,
          settings: {
            mature: mature,
          },
        },
      },
      code: "story/create",
    };
  }

  function handleChange(
    event: React.ChangeEvent<HTMLInputElement>,
    char: Character
  ) {
    let charId = parseInt(event.target.id);
    let s = new Map<number, Character>(picks);
    if (!event.target.checked) {
      s.delete(charId);
      setPicks(s);
    } else {
      if (picks.size >= numChoices) {
        let next = s.keys().next();
        if (next.value) {
          s.delete(next.value);
        }
      }
      s.set(charId, char);
      setPicks(s);
    }
  }

  function checked(id: number): boolean {
    return picks.has(id) || picks.has(id);
  }

  function allAges() {
    return settings.maturity === Maturity.ALL_AGES;
  }

  function handleMature(event: React.ChangeEvent<HTMLInputElement>) {
    setMature(event.target.value === "true");
  }

  const { isReady } = useFormValidation({
    validationRules: [
      {
        validate: () =>
          settings.num_characters_select
            ? picks.size >= settings.num_characters_select
            : false,
      },
      {
        validate: () =>
          settings.maturity === Maturity.ALL_AGES || mature !== undefined,
      },
    ],
    dependencies: [picks, mature, settings],
  });

  function subHeaders() {
    const lines = [];

    // Since we only support a single value now, always show single number
    lines.push(
      t("createStory.subheaders.default", {
        number: settings.num_characters_select,
      })
    );

    if (isSolo) {
      return lines;
    }
    lines.push(t("createStory.subheaders.guide"));
    if (!allAges()) {
      lines.push(t("createStory.subheaders.maturity"));
    }
    return lines;
  }

  const { waitableProps, formPageProps } = useWaitableForm({
    gameSubmit,
    waitingFor: [GameStatus.GameStatusPostStoryCreate],
    waitHeader: t("createStory.headers.waiting"),
    titles: [t("createStory.headers.default")],
    submitText: t("createStory.footers.default"),
    validation: { isReady },
    subHeaders: subHeaders(),
  });

  return (
    <Waitable playerActionHandler={submit} {...waitableProps}>
      <FormPage {...formPageProps}>
        <div
          className={"grow flex flex-col justify-around p-2 max-w-full"}
        >
          <div className={"flex flex-col space-y-3 max-w-full"}>
            {currentStory?.characters &&
              currentStory.characters.map((c) => (
                <div
                  className={
                    "flex flex-row-reverse items-stretch justify-between mx-4 mt-2 max-w-full"
                  }
                  key={c.id}
                >
                  {settings.character_maturity && (
                    <FakeButton className="w-24 flex justify-center items-center leading-none p-0.5 ml-2">
                      {c.meta.mature ? "18+" : "all ages"}
                    </FakeButton>
                  )}
                  <input
                    className={"cursor-pointer self-center ml-1"}
                    style={{ zoom: 2 }}
                    id={"" + c.id}
                    type="checkbox"
                    checked={checked(c.id)}
                    onChange={(e) => handleChange(e, c)}
                  />
                  <label
                    className={`text-2xl cursor-pointer outline-1 pl-1 flex justify-center items-center leading-relaxed max-w-full break-all grow ${checked(c.id)
                      ? "outline-black outline-2 bg-night-primary"
                      : "outline-night-primary"
                      }`}
                    htmlFor={"" + c.id}
                  >
                    {c.name}
                  </label>
                </div>
              ))}
          </div>

          {!allAges() && !isSolo ? (
            <div className={"self-stretch flex flex-row"}>
              <div
                className={
                  "grow flex flex-row items-center justify-evenly"
                }
              >
                <FakeButton size="2xl" className="p-1 mr-2">
                  {t("createStory.body.maturity")}
                </FakeButton>

                <div className={"flex flex-row items-center"}>
                  <div className={"flex flex-row items-stretch cursor-pointer"}>
                    <input
                      id="mature-yes"
                      name="mature"
                      type="radio"
                      value="true"
                      onChange={handleMature}
                      disabled={hasAllAges}
                      checked={mature === true}
                      className="opacity-0 h-0 w-0 m-0"
                    />
                    <label
                      htmlFor="mature-yes"
                      className={`text-lg cursor-pointer p-1 border border-solid border-[#444] ml-1 leading-none flex justify-center items-center mx-1 ${mature === true
                        ? "bg-gray-drk outline-4 outline-[#444]"
                        : "bg-gray-light"
                        } ${hasAllAges ? "opacity-50" : ""}`}
                    >
                      18+
                    </label>
                    <input
                      id="mature-no"
                      name="mature"
                      type="radio"
                      value="false"
                      onChange={handleMature}
                      disabled={hasAllAges}
                      checked={mature === false}
                      className="opacity-0 h-0 w-0 m-0"
                    />
                    <label
                      htmlFor="mature-no"
                      className={`text-lg cursor-pointer p-1 border border-solid border-[#444] ml-1 leading-none flex justify-center items-center mx-1 ${mature === false
                        ? "bg-gray-drk outline-4 outline-[#444]"
                        : "bg-gray-light"
                        } ${hasAllAges ? "opacity-50" : ""}`}
                    >
                      all ages
                    </label>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            ""
          )}
        </div>
      </FormPage>
    </Waitable>
  );
};

export default CreateStory;
